import { useQuery } from "@tanstack/react-query";
import { api } from "lib/api";
import type { SharedEntityMeta } from "types/chat";
import { apiRoutes } from "util/apiRoutes";
import { parseSharedEntityUrl } from "util/sharedEntity";
import type { Action, Comment, Feedback, Impact, Issue, Solution } from "util/constants";

interface UserPreviewResponse {
  username: string;
  display_name?: string | null;
  about?: string | null;
  bio?: string | null;
  properties?: Record<string, unknown> | null;
  properties_public?: Record<string, unknown> | null;
}

interface TeamPreviewResponse {
  teamname: string;
  display_name?: string | null;
  about?: string | null;
  properties?: Record<string, unknown> | null;
  properties_public?: Record<string, unknown> | null;
}

interface QuizPreviewResponse {
  data: {
    quiz: {
      code: string;
      participant_count: number;
    };
  };
}

const IMAGE_CDN = String(process.env.REACT_APP_IMAGE_CDN || "").replace(/\/$/, "");

const stripHtmlToText = (value?: string | null): string =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const prefixCdnIfNeeded = (value?: string | null): string | undefined => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) {
    return undefined;
  }

  if (/^https?:\/\//i.test(normalizedValue)) {
    return normalizedValue;
  }

  if (!IMAGE_CDN) {
    return undefined;
  }

  return `${IMAGE_CDN}/${normalizedValue.replace(/^\//, "")}`;
};

const getIssueImageUrl = (issue: Issue): string | undefined =>
  prefixCdnIfNeeded(
    issue?.properties?.reference_link_img_key ||
      issue?.properties?.subject_properties?.thumbnail?.source ||
      issue?.properties?.subject_properties?.wiki_parse_first_img
  );

const getPostImageUrl = (
  post: Impact | Solution | Feedback | Action
): string | undefined => {
  const mediaFiles: Array<{ type?: string; key?: string }> = Array.isArray(post?.properties?.media_files)
    ? (post.properties.media_files as Array<{ type?: string; key?: string }>)
    : [];
  const primaryImage = mediaFiles.find((file) => file?.type === "image");

  return prefixCdnIfNeeded(
    post?.properties?.reference_link_img_key ||
      (typeof primaryImage?.key === "string" ? primaryImage.key : undefined)
  );
};

const getAvatarKey = (value?: Record<string, unknown> | null): string | undefined => {
  const avatarKey = value?.avatar_key;
  return typeof avatarKey === "string" && avatarKey.trim()
    ? avatarKey
    : undefined;
};

const getCommentCount = (value: { comment_count?: number } | null | undefined): number =>
  typeof value?.comment_count === "number" ? value.comment_count || 0 : 0;

const buildIssuePreview = (issue: Issue): SharedEntityMeta => ({
  entity_type: "issue",
  entity_id: issue.cname || issue.id,
  entity_cname: issue.cname,
  title: issue.name || issue.cname,
  description: stripHtmlToText(
    issue.description ||
      issue.properties?.subject_properties?.extract ||
      issue.properties?.wiki_summary
  ),
  image_url: getIssueImageUrl(issue),
  vote_count: issue.vote_count || 0,
  comment_count: getCommentCount(issue as Issue & { comment_count?: number }),
  canonical_path: `/issue/${encodeURIComponent(issue.cname)}`,
  share_path: `/s/issue/${encodeURIComponent(issue.cname)}`,
});

const buildPostPreview = (
  entityType: string,
  post: Impact | Solution | Feedback | Action
): SharedEntityMeta => {
  // Prefer the canonical `post.kind` over an upstream `entityType` arg so
  // teabate posts surface as `entity_type='teabate'` even if the caller
  // passed a stale `'impact'` value.
  const resolvedEntityType =
    post.kind === "teabate" ? "teabate" : entityType;

  return {
    entity_type: resolvedEntityType,
    entity_id: post.id,
    title: post.title || stripHtmlToText(post.body || ""),
    description: stripHtmlToText(post.body || post.description || ""),
    image_url: getPostImageUrl(post),
    author_username:
      post.properties?.privacy === "anonymous" ? undefined : post.username || undefined,
    author_avatar_key:
      post.properties?.privacy === "anonymous"
        ? undefined
        : getAvatarKey(post.properties_public),
    vote_count: post.vote_count || 0,
    comment_count: getCommentCount(post as typeof post & { comment_count?: number }),
    canonical_path: `/${resolvedEntityType}/${encodeURIComponent(String(post.id))}`,
    share_path: `/s/${resolvedEntityType}/${encodeURIComponent(String(post.id))}`,
  };
};

const buildCollectionPreview = (post: Impact | Solution | Feedback | Action): SharedEntityMeta => ({
  entity_type: "collection",
  entity_id: post.id,
  title: post.title || "Collection",
  description: stripHtmlToText(post.body || post.description || ""),
  image_url: getPostImageUrl(post),
  author_username:
    post.properties?.privacy === "anonymous" ? undefined : post.username || undefined,
  author_avatar_key:
    post.properties?.privacy === "anonymous"
      ? undefined
      : getAvatarKey(post.properties_public),
  vote_count: post.vote_count || 0,
  comment_count: getCommentCount(post as typeof post & { comment_count?: number }),
  canonical_path: `/collection/${encodeURIComponent(String(post.id))}`,
  share_path: `/s/collection/${encodeURIComponent(String(post.id))}`,
});

const buildCommentPreview = (comment: Comment): SharedEntityMeta => ({
  entity_type: "comment",
  entity_id: comment.id,
  title: "Shared comment",
  description: stripHtmlToText(comment.content),
  author_username: comment.username || undefined,
  author_avatar_key: getAvatarKey(comment.properties_public),
  canonical_path: `/comment/${encodeURIComponent(String(comment.id))}`,
  share_path: `/s/comment/${encodeURIComponent(String(comment.id))}`,
});

const buildUserPreview = (user: UserPreviewResponse): SharedEntityMeta => ({
  entity_type: "user",
  entity_id: user.username,
  title: user.display_name || user.username,
  description: stripHtmlToText(
    user.about ||
      user.bio ||
      (typeof user.properties?.summary === "string" ? user.properties.summary : "")
  ),
  author_username: user.username,
  author_avatar_key: getAvatarKey(user.properties_public),
  canonical_path: `/user/${encodeURIComponent(user.username)}`,
  share_path: `/s/user/${encodeURIComponent(user.username)}`,
});

const buildTeamPreview = (team: TeamPreviewResponse): SharedEntityMeta => ({
  entity_type: "team",
  entity_id: team.teamname,
  title: team.display_name || team.teamname,
  description: stripHtmlToText(
    team.about ||
      (typeof team.properties?.summary === "string" ? team.properties.summary : "")
  ),
  author_username: team.display_name || team.teamname,
  author_avatar_key: getAvatarKey(team.properties_public),
  canonical_path: `/partea/${encodeURIComponent(team.teamname)}`,
  share_path: `/s/partea/${encodeURIComponent(team.teamname)}`,
});

const buildQuizPreview = (quizResponse: QuizPreviewResponse): SharedEntityMeta => ({
  entity_type: "quiz",
  entity_id: quizResponse.data.quiz.code,
  title: `Quiz ${quizResponse.data.quiz.code.toUpperCase()}`,
  description: `Join this Societea quiz with ${quizResponse.data.quiz.participant_count} participant${quizResponse.data.quiz.participant_count === 1 ? "" : "s"}.`,
  canonical_path: `/quiz/${encodeURIComponent(quizResponse.data.quiz.code)}`,
  share_path: `/s/quiz/${encodeURIComponent(quizResponse.data.quiz.code)}`,
});

/**
 * Load a normalized shared-entity preview for a Societea URL.
 */
export const useEntityPreview = (
  urlValue: string | null | undefined,
  enabled = true
) => {
  const parsedUrl = urlValue ? parseSharedEntityUrl(urlValue) : null;

  return useQuery({
    queryKey: ["chat", "entity-preview", parsedUrl?.entityType, parsedUrl?.entityId],
    enabled: enabled && parsedUrl !== null,
    staleTime: 60_000,
    queryFn: async (): Promise<SharedEntityMeta | null> => {
      if (!parsedUrl) {
        return null;
      }

      switch (parsedUrl.entityType) {
        case "issue": {
          const response = await api.get<Issue>(apiRoutes.issues.byName(parsedUrl.entityId));
          return buildIssuePreview(response.data);
        }
        case "impact":
        case "teabate":
        case "solution":
        case "action":
        case "feedback": {
          const response = await api.get<Impact | Solution | Feedback | Action>(
            apiRoutes.posts.byId(parsedUrl.entityId)
          );
          return buildPostPreview(parsedUrl.entityType, response.data);
        }
        case "collection": {
          const response = await api.get<Impact | Solution | Feedback | Action>(
            apiRoutes.posts.byId(parsedUrl.entityId)
          );
          return buildCollectionPreview(response.data);
        }
        case "comment": {
          const response = await api.get<Comment>(apiRoutes.comments.byId(parsedUrl.entityId));
          return buildCommentPreview(response.data);
        }
        case "user": {
          const response = await api.get<UserPreviewResponse>(
            apiRoutes.user.byUsername(parsedUrl.entityId)
          );
          return buildUserPreview(response.data);
        }
        case "team": {
          const response = await api.get<TeamPreviewResponse>(
            apiRoutes.team.byName(parsedUrl.entityId)
          );
          return buildTeamPreview(response.data);
        }
        case "quiz": {
          const response = await api.get<QuizPreviewResponse>(
            apiRoutes.quiz.byCode(parsedUrl.entityId)
          );
          return buildQuizPreview(response.data);
        }
        default:
          return null;
      }
    },
  });
};
